(() => {
class AxisMundiChapterOne {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.total = Number(section.dataset.total || 0);
    this.storageKey = `axis-mundi-chapter-one:${this.sectionId}`;
    this.revealPhrase = section.dataset.revealPhrase || 'FALSE LIGHT';
    this.glyphs = Array.from(section.querySelectorAll('[data-axis-glyph]'));
    this.slots = Array.from(section.querySelectorAll('[data-axis-slot]'));
    this.foundSlots = Array.from(section.querySelectorAll('[data-axis-found-slot]'));
    this.selectedSlotIndex = null;
    this.foundEl = section.querySelector('[data-axis-found]');
    this.fillEl = section.querySelector('[data-axis-fill]');
    this.revealEl = section.querySelector('[data-axis-reveal]');
    this.phraseEl = section.querySelector('[data-axis-phrase]');
    this.stage = section.querySelector('[data-axis-stage]');
    this.foundRail = section.querySelector('.axis-chapter__found-rail');
    this.readout = section.querySelector('[data-axis-readout]');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isComplete = false;
    this.isAnimatingCompletion = false;

    this.state = this.readState();
    this.bindEvents();
    this.render();

    this.checkCompletion(false);
  }

  bindEvents() {
    this.glyphs.forEach((glyph) => {
      glyph.addEventListener('click', () => this.collect(glyph));
    });

    this.slots.forEach((slot) => {
      slot.addEventListener('click', () => this.selectBankSlot(slot));
    });

    const resetButton = this.section.querySelector('[data-axis-reset]');
    if (resetButton) {
      resetButton.addEventListener('click', () => this.reset());
    }

    if (this.stage && this.readout && this.section.dataset.developer === 'true') {
      this.stage.addEventListener('pointermove', (event) => this.updateReadout(event));
    }

    this.syncFoundRailHeight();

    if (this.stage && this.foundRail && 'ResizeObserver' in window) {
      new ResizeObserver(() => this.syncFoundRailHeight()).observe(this.stage);
    } else {
      window.addEventListener('resize', () => this.syncFoundRailHeight());
    }
  }

  syncFoundRailHeight() {
    if (!this.stage || !this.foundRail) return;

    const stageHeight = this.stage.getBoundingClientRect().height;
    if (stageHeight > 0) {
      this.foundRail.style.setProperty('--axis-found-rail-height', `${stageHeight}px`);
    }
  }

  readState() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(this.storageKey));
      if (saved && Array.isArray(saved.collected)) {
        return {
          collected: saved.collected
            .map((item) => Number(item))
            .filter((item, index, array) => Number.isInteger(item) && item >= 0 && item < this.total && array.indexOf(item) === index),
        };
      }
    } catch (error) {
      // Ignore corrupt localStorage and start clean.
    }

    return { collected: [] };
  }

  saveState() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (error) {
      // localStorage can be unavailable in private browsing; interaction should still work for this session.
    }
  }

  reset() {
    try {
      window.localStorage.removeItem(this.storageKey);
      window.localStorage.removeItem(`axis-mundi-world:${this.sectionId}`);
      window.localStorage.removeItem(`album-landing-entered:${this.sectionId}`);
    } catch (error) {
      // Reloading still restores the initial in-memory state when storage is unavailable.
    }

    window.location.reload();
  }

  collect(glyph) {
    if (this.isComplete || this.isAnimatingCompletion) return;

    const index = Number(glyph.dataset.index);
    if (!Number.isInteger(index) || this.state.collected.includes(index)) return;

    glyph.classList.add('is-collecting');
    this.state.collected.push(index);
    this.saveState();
    this.render();

    window.setTimeout(() => {
      glyph.classList.remove('is-collecting');
      glyph.classList.add('is-collected');
    }, this.prefersReducedMotion ? 0 : 380);

    this.checkCompletion(true);
  }

  render() {
    const found = this.state.collected.length;
    const percent = this.total > 0 ? Math.min(100, (found / this.total) * 100) : 0;

    if (this.foundEl) this.foundEl.textContent = found;
    if (this.fillEl) this.fillEl.style.width = `${percent}%`;

    this.glyphs.forEach((glyph, index) => {
      const collected = this.state.collected.includes(index);
      glyph.classList.toggle('is-collected', collected);
    });

    this.renderBank();
    this.renderFoundRail();
  }

  renderBank() {
    this.slots.forEach((slot) => {
      slot.textContent = '';
      slot.setAttribute('aria-hidden', 'true');
      slot.removeAttribute('data-axis-bank-index');
      slot.classList.remove('is-filled', 'is-selected');
    });

    this.state.collected.forEach((glyphIndex, slotIndex) => {
      const slot = this.slots[slotIndex];
      const glyph = this.glyphs[glyphIndex];
      const image = glyph ? glyph.querySelector('img') : null;
      if (!slot || !image) return;

      const bankImage = image.cloneNode(false);
      bankImage.className = 'axis-chapter__bank-img';
      bankImage.removeAttribute('loading');
      bankImage.alt = '';
      slot.appendChild(bankImage);
      slot.removeAttribute('aria-hidden');
      slot.setAttribute('aria-label', `Recovered glyph ${glyph.dataset.letter}`);
      slot.dataset.axisBankIndex = slotIndex;
      slot.classList.add('is-filled');
      slot.classList.toggle('is-selected', slotIndex === this.selectedSlotIndex);
    });
  }

  renderFoundRail() {
    this.foundSlots.forEach((slot) => {
      slot.textContent = '';
      slot.setAttribute('aria-hidden', 'true');
    });

    this.state.collected.forEach((glyphIndex, foundIndex) => {
      const slot = this.foundSlots[foundIndex];
      const glyph = this.glyphs[glyphIndex];
      const image = glyph ? glyph.querySelector('img') : null;
      if (!slot || !image) return;

      const foundImage = image.cloneNode(false);
      foundImage.className = 'axis-chapter__found-img';
      foundImage.removeAttribute('loading');
      foundImage.alt = '';
      slot.appendChild(foundImage);
      slot.removeAttribute('aria-hidden');
    });
  }

  selectBankSlot(slot) {
    if (this.isComplete || this.isAnimatingCompletion) return;

    const slotIndex = Number(slot.dataset.axisBankIndex);
    if (!Number.isInteger(slotIndex)) return;

    if (this.selectedSlotIndex === null) {
      this.selectedSlotIndex = slotIndex;
      this.renderBank();
      return;
    }

    if (this.selectedSlotIndex !== slotIndex) {
      const selectedGlyph = this.state.collected[this.selectedSlotIndex];
      this.state.collected[this.selectedSlotIndex] = this.state.collected[slotIndex];
      this.state.collected[slotIndex] = selectedGlyph;
      this.saveState();
    }

    this.selectedSlotIndex = null;
    this.renderBank();
    this.checkCompletion(true);
  }

  checkCompletion(animate) {
    if (this.total <= 0 || this.state.collected.length < this.total) return;

    const recoveredPhrase = this.state.collected
      .map((glyphIndex) => this.glyphs[glyphIndex]?.dataset.letter || '')
      .join('');
    const targetPhrase = this.revealPhrase.replace(/\s/g, '');

    if (recoveredPhrase === targetPhrase) this.complete(animate);
  }

  complete(animate = true) {
    if (this.isComplete || this.isAnimatingCompletion) return;

    this.isAnimatingCompletion = true;
    this.isComplete = true;
    this.section.dataset.complete = 'true';
    this.section.classList.add('is-complete');
    if (this.revealEl) this.revealEl.hidden = false;

    if (!animate || this.prefersReducedMotion) {
      this.showPhrase();
      this.showCopy();
      this.isAnimatingCompletion = false;
      return;
    }

    window.setTimeout(() => {
      this.section.classList.add('is-resolving');
    }, 500);

    window.setTimeout(() => {
      this.showPhrase();
    }, 1850);

    window.setTimeout(() => {
      this.showCopy();
      this.isAnimatingCompletion = false;
      this.dispatchComplete();
    }, 2500);
  }

  showPhrase() {
    if (this.phraseEl) {
      this.phraseEl.textContent = this.revealPhrase;
      this.phraseEl.removeAttribute('aria-hidden');
    }
    this.section.classList.add('is-phrase-visible');
  }

  showCopy() {
    this.section.classList.add('is-copy-visible');
    this.dispatchComplete();
  }

  dispatchComplete() {
    if (this.hasDispatchedComplete) return;
    this.hasDispatchedComplete = true;
    this.section.dispatchEvent(new CustomEvent('axis:chapter-complete', {
      bubbles: true,
      detail: {
        chapter: 'chapter-1',
      },
    }));
  }

  updateReadout(event) {
    const rect = this.stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.readout.textContent = `${Math.max(0, Math.min(100, x)).toFixed(1)}, ${Math.max(0, Math.min(100, y)).toFixed(1)}`;
  }
}

function initAxisMundiChapterOne(container = document) {
  container.querySelectorAll('[data-axis-chapter]').forEach((section) => {
    if (section.axisMundiChapterOne) return;
    section.axisMundiChapterOne = new AxisMundiChapterOne(section);
  });
}

document.addEventListener('DOMContentLoaded', () => initAxisMundiChapterOne());
document.addEventListener('shopify:section:load', (event) => initAxisMundiChapterOne(event.target));
})();
